<template>
    <div class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-y-auto">
        <form @submit.prevent="saveSettings">
            <div class="px-8 sm:px-8 lg:px-8 pt-6 pb-2">
                <div class="w-full xl:w-4/5 mx-auto flex items-center justify-between gap-4">
                    <div class="min-w-0 flex-1">
                        <h1 class="stage-page-title">{{ $t('Server Monitoring Settings') }}</h1>
                    </div>
                    <div class="flex items-center">
                        <button class="btn p-4 rounded-none" type="button" @click="goBack">
                            <svg-vue class="h-5 w-5 text-gray-700" icon="font-awesome.chevron-left-regular"></svg-vue>
                        </button>
                        <div v-on-clickaway="closeActionDropdown" class="block">
                            <div class="relative inline-block text-left">
                                <button class="btn p-4 rounded-none" type="button" @click="toggleActionDropdown('employees')">
                                    <svg-vue class="h-5 w-5 text-gray-700" icon="font-awesome.user-tag-regular"></svg-vue>
                                </button>
                                <div v-show="actions.employees" class="origin-top-right absolute right-0 mt-1 w-64 rounded shadow-lg z-20">
                                    <div class="rounded bg-white shadow-xs">
                                        <div class="stage-icon-menu-header">{{ $t('Employee assignment') }}</div>
                                        <div class="stage-dropdown-menu max-h-80 overflow-y-auto">
                                            <template v-for="employee in employeeOptions" :key="'server-monitoring-employee-' + employee.id">
                                                <label
                                                   
                                                    class="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                                                >
                                                    <input
                                                        v-model="selectedEmployeeIds"
                                                        :value="employee.id"
                                                        class="form-checkbox h-4 w-4 text-gray-600 transition duration-150 ease-in-out mr-3"
                                                        type="checkbox"
                                                        @change="onEmployeeAssignmentsChange"
                                                    >
                                                    <img
                                                        :alt="employee.name"
                                                        :src="resolveAssignmentAvatar(employee)"
                                                        class="h-6 w-6 rounded-full object-cover mr-2"
                                                    />
                                                    <span class="truncate">{{ employee.name }}</span>
                                                </label>
                                            </template>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="relative inline-block text-left">
                                <button class="btn p-4 rounded-none" type="button" @click="toggleActionDropdown('customers')">
                                    <svg-vue class="h-5 w-5 text-gray-700" icon="font-awesome.users-class-regular"></svg-vue>
                                </button>
                                <div v-show="actions.customers" class="origin-top-right absolute right-0 mt-1 w-64 rounded shadow-lg z-20">
                                    <div class="rounded bg-white shadow-xs">
                                        <div class="stage-icon-menu-header">{{ $t('Customer assignment') }}</div>
                                        <div class="stage-dropdown-menu max-h-80 overflow-y-auto">
                                            <template v-for="customer in customerOptions" :key="'server-monitoring-customer-' + customer.id">
                                                <label
                                                   
                                                    class="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                                                >
                                                    <input
                                                        v-model="selectedCustomerIds"
                                                        :value="customer.id"
                                                        class="form-checkbox h-4 w-4 text-gray-600 transition duration-150 ease-in-out mr-3"
                                                        type="checkbox"
                                                        @change="onCustomerAssignmentsChange"
                                                    >
                                                    <img
                                                        :alt="customer.name"
                                                        :src="resolveAssignmentAvatar(customer)"
                                                        class="h-6 w-6 rounded-full object-cover mr-2"
                                                    />
                                                    <span class="truncate">{{ customer.name }}</span>
                                                </label>
                                            </template>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="stage-page-body">
                <template v-if="site">
                    <div class="w-full xl:w-4/5 mx-auto space-y-6">
                        <div class="bg-white shadow sm:rounded border border-bordergray-100 overflow-hidden">
                            <div class="p-6 md:grid md:grid-cols-3 md:gap-6">
                                <div class="md:col-span-1">
                                    <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Website') }}</h3>
                                </div>
                                <div class="mt-5 md:mt-0 md:col-span-2">
                                    <div class="grid grid-cols-1 gap-5">
                                        <div>
                                            <label class="stage-filter-label block mb-2">{{ $t('Title') }}</label>
                                            <input v-model.trim="form.website.title" class="stage-filter-input form-input block w-full text-sm" maxlength="255" type="text">
                                        </div>
                                        <div>
                                            <label class="stage-filter-label block mb-2">URL</label>
                                            <input v-model.trim="form.external.url" class="stage-filter-input form-input block w-full text-sm" type="url">
                                        </div>
                                        <div>
                                            <label class="stage-filter-label block mb-2">{{ $t('Environment') }}</label>
                                            <select v-model="form.website.environment" class="stage-filter-input form-select block w-full text-sm">
                                                <option value="production">{{ $t('Production') }}</option>
                                                <option value="staging">Staging</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="border-t border-bordergray-100 p-6 md:grid md:grid-cols-3 md:gap-6">
                                <div class="md:col-span-1">
                                    <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Customers') }}</h3>
                                </div>
                                <div class="mt-5 md:mt-0 md:col-span-2">
                                    <div class="border border-bordergray-100 rounded overflow-y-auto max-h-80 bg-white divide-y divide-bordergray-100">
                                        <label
                                            v-for="customer in customerOptions"
                                            :key="'server-monitoring-settings-customer-' + customer.id"
                                            class="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                                        >
                                            <input
                                                v-model="selectedCustomerIds"
                                                :value="customer.id"
                                                class="form-checkbox h-4 w-4 text-gray-600 transition duration-150 ease-in-out mr-3"
                                                type="checkbox"
                                                @change="onCustomerAssignmentsChange"
                                            >
                                            <img
                                                :alt="customer.name"
                                                :src="resolveAssignmentAvatar(customer)"
                                                class="h-6 w-6 rounded-full object-cover mr-2"
                                            >
                                            <span class="truncate">{{ customer.name }}</span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                            <div class="stage-footer-surface text-right px-6 py-3 border-t border-bordergray-100">
                                <button class="btn-stage-primary" type="submit">{{ $t('Save') }}</button>
                            </div>
                        </div>

                        <div class="bg-white shadow sm:rounded border border-bordergray-100 overflow-hidden">
                            <div class="p-6 md:grid md:grid-cols-3 md:gap-6">
                                <div class="md:col-span-1">
                                    <h3 class="text-lg font-medium leading-6 text-gray-900">Externes Monitoring</h3>
                                    <div class="mt-2 text-sm text-gray-500 leading-5">
                                        {{ $t('TLS and reachability checks without an agent on the target server') }}.
                                    </div>
                                </div>
                                <div class="mt-5 md:mt-0 md:col-span-2">
                                    <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        <div>
                                            <label class="stage-filter-label block mb-2">{{ $t('Interval (seconds)') }}</label>
                                            <input v-model.number="form.external.interval_seconds" class="stage-filter-input form-input block w-full text-sm" min="30" step="30" type="number">
                                        </div>
                                        <div>
                                            <label class="stage-filter-label block mb-2">Timeout (ms)</label>
                                            <input v-model.number="form.external.timeout_ms" class="stage-filter-input form-input block w-full text-sm" min="1000" step="500" type="number">
                                        </div>
                                        <div>
                                            <label class="stage-filter-label block mb-2">{{ $t('TLS warning (days)') }}</label>
                                            <input v-model.number="form.external.tls_warn_days" class="stage-filter-input form-input block w-full text-sm" min="1" step="1" type="number">
                                        </div>
                                        <div class="flex items-end">
                                            <label class="inline-flex items-center">
                                                <input v-model="form.external.enabled" class="form-checkbox h-4 w-4 text-gray-600" type="checkbox">
                                                <span class="ml-2 text-sm text-gray-800">{{ $t('Monitoring enabled') }}</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="stage-footer-surface text-right px-6 py-3 border-t border-bordergray-100">
                                <button class="btn-stage-primary" type="submit">{{ $t('Save') }}</button>
                            </div>
                        </div>

                        <div class="bg-white shadow sm:rounded border border-bordergray-100 overflow-hidden">
                            <div class="p-6 md:grid md:grid-cols-3 md:gap-6">
                                <div class="md:col-span-1">
                                    <h3 class="text-lg font-medium leading-6 text-gray-900">Server-Agent</h3>
                                    <div class="mt-2 text-sm text-gray-500 leading-5">
                                        {{ $t('Generate agent code for PHP or Python (minimal, safe, without sensitive system data)') }}.
                                    </div>
                                </div>
                                <div class="mt-5 md:mt-0 md:col-span-2">
                                    <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        <div class="md:col-span-2">
                                            <label class="stage-filter-label block mb-2">Agent Endpoint URL</label>
                                            <input v-model.trim="form.agent.endpoint" class="stage-filter-input form-input block w-full text-sm" type="url">
                                        </div>
                                        <div>
                                            <label class="stage-filter-label block mb-2">Agent Version</label>
                                            <input v-model.trim="form.agent.version" class="stage-filter-input form-input block w-full text-sm" type="text">
                                        </div>
                                        <div>
                                            <label class="stage-filter-label block mb-2">Shared Secret</label>
                                            <input v-model.trim="form.agent.secret" autocomplete="off" class="stage-filter-input form-input block w-full text-sm" type="text">
                                        </div>
                                        <div class="md:col-span-2">
                                            <div class="flex items-center gap-4">
                                                <label class="inline-flex items-center">
                                                    <input v-model="agentLanguage" class="form-radio h-4 w-4 text-gray-700" type="radio" value="php">
                                                    <span class="ml-2 text-sm text-gray-800">PHP</span>
                                                </label>
                                                <label class="inline-flex items-center">
                                                    <input v-model="agentLanguage" class="form-radio h-4 w-4 text-gray-700" type="radio" value="python">
                                                    <span class="ml-2 text-sm text-gray-800">Python</span>
                                                </label>
                                                <div class="ml-auto flex items-center gap-2">
                                                    <button class="btn-stage-secondary" type="button" @click="generateAgentCode">{{ $t('Generate agent code') }}</button>
                                                    <button class="btn-stage-secondary" type="button" @click="downloadAgentCode">Download</button>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="md:col-span-2">
                                            <label class="stage-filter-label block mb-2">Code-Vorschau</label>
                                            <textarea
                                                v-model="generatedAgentCode"
                                                class="stage-filter-input form-input block w-full font-mono text-xs bg-gray-50"
                                                rows="16"
                                                readonly
                                            ></textarea>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="stage-footer-surface text-right px-6 py-3 border-t border-bordergray-100">
                                <button class="btn-stage-primary" type="submit">{{ $t('Save') }}</button>
                            </div>
                        </div>

                        <div class="bg-white shadow sm:rounded border border-bordergray-100 overflow-hidden">
                            <div class="p-6 md:grid md:grid-cols-3 md:gap-6">
                                <div class="md:col-span-1">
                                    <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Git repository analysis') }}</h3>
                                    <div class="mt-2 text-sm text-gray-500 leading-5">
                                        {{ $t('Read-only analysis via the GitHub API on manifest and commit level') }}.
                                    </div>
                                </div>
                                <div class="mt-5 md:mt-0 md:col-span-2">
                                    <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        <div>
                                            <label class="stage-filter-label block mb-2">Provider</label>
                                            <select v-model="form.repository.provider" class="stage-filter-input form-select block w-full text-sm">
                                                <option value="github">GitHub</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label class="stage-filter-label block mb-2">Default Branch</label>
                                            <input v-model.trim="form.repository.branch" class="stage-filter-input form-input block w-full text-sm" type="text">
                                        </div>
                                        <div class="md:col-span-2">
                                            <label class="stage-filter-label block mb-2">Repository URL</label>
                                            <input v-model.trim="form.repository.url" class="stage-filter-input form-input block w-full text-sm" type="url">
                                        </div>
                                        <div class="md:col-span-2">
                                            <label class="stage-filter-label block mb-2">Read Token</label>
                                            <input v-model.trim="form.repository.token" autocomplete="off" class="stage-filter-input form-input block w-full text-sm" type="password">
                                        </div>
                                        <div class="md:col-span-2">
                                            <button class="btn-stage-secondary" type="button" @click="runRepoCheck">
                                                {{ $t('Test repository analysis') }}
                                            </button>
                                        </div>
                                        <div class="md:col-span-2 border border-bordergray-100 rounded bg-gray-100 p-3">
                                            <div class="text-xs text-gray-500">Status</div>
                                            <div class="mt-2 text-sm text-gray-700">{{ repoCheckMessage }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="stage-footer-surface text-right px-6 py-3 border-t border-bordergray-100">
                                <button class="btn-stage-primary" type="submit">{{ $t('Save') }}</button>
                            </div>
                        </div>
                    </div>
                </template>

                <template v-else>
                    <div class="w-full xl:w-4/5 mx-auto bg-white shadow sm:rounded border border-bordergray-100 p-8 text-center">
                        <div class="text-xl font-semibold text-gray-800">{{ $t('Website not found') }}</div>
                        <router-link class="btn-stage-primary mt-4 inline-flex" to="/workspace/insights/server-monitoring">
                            {{ $t('Back to overview') }}
                        </router-link>
                    </div>
                </template>
            </div>
        </form>
    </div>
</template>

<script>
import {mixin as clickaway} from "vue-clickaway";

export default {
    name: "server-monitoring-settings",
    mixins: [clickaway],
    metaInfo() {
        return {
            title: this.site ? this.site.name + ' | ' + this.$t('Monitoring Settings') : this.$t('Monitoring Settings').toString()
        }
    },
    data() {
        return {
            loading: false,
            site: null,
            agentLanguage: 'php',
            generatedAgentCode: '',
            repoCheckMessage: this.$t('Not executed yet').toString(),
            actions: {
                employees: false,
                customers: false,
            },
            assignmentSaving: false,
            assignmentSavePending: false,
            assignmentPendingMessage: '',
            selectedEmployeeIds: [],
            selectedCustomerIds: [],
            employeeOptions: [],
            customerOptions: [],
            form: {
                website: {
                    title: '',
                    environment: 'production',
                },
                external: {
                    url: '',
                    interval_seconds: 300,
                    timeout_ms: 5000,
                    tls_warn_days: 14,
                    enabled: true,
                },
                agent: {
                    endpoint: '',
                    version: '1.0.0',
                    secret: '',
                },
                repository: {
                    provider: 'github',
                    branch: 'main',
                    url: '',
                    token: '',
                }
            },
        };
    },
    watch: {
        '$route.params.siteId': {
            immediate: true,
            handler(siteId) {
                this.loadSite(siteId);
                this.loadOptions();
            }
        }
    },
    methods: {
        loadSite(siteId) {
            if (!siteId) {
                this.site = null;
                return;
            }

            const self = this;
            self.loading = true;
            axios.get('api/workspace/insights/server-monitoring/' + siteId).then(function (response) {
                self.site = response.data || null;
                self.applySiteDefaults();
                self.loading = false;
            }).catch(function () {
                self.site = null;
                self.loading = false;
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: self.$t('Settings could not be loaded').toString(),
                    type: 'error'
                });
            });
        },
        loadOptions() {
            const self = this;
            axios.get('api/workspace/insights/server-monitoring/options').then(function (response) {
                self.customerOptions = Array.isArray(response.data && response.data.customers)
                    ? response.data.customers
                    : [];
                self.employeeOptions = Array.isArray(response.data && response.data.employees)
                    ? response.data.employees
                    : [];
            }).catch(function () {
                self.customerOptions = [];
                self.employeeOptions = [];
            });
        },
        applySiteDefaults() {
            if (!this.site) {
                return;
            }

            this.form.website.title = this.site.name || '';
            this.form.website.environment = this.site.environment || 'production';

            const settings = this.site.settings || {};
            const externalSettings = settings.external || {};
            const agentSettings = settings.agent || {};
            const repositorySettings = settings.repository || {};

            this.form.external.url = externalSettings.url || this.buildDefaultUrl(this.site.domain);
            this.form.external.interval_seconds = this.toIntOrDefault(externalSettings.interval_seconds, 300);
            this.form.external.timeout_ms = this.toIntOrDefault(externalSettings.timeout_ms, 5000);
            this.form.external.tls_warn_days = this.toIntOrDefault(externalSettings.tls_warn_days, 14);
            this.form.external.enabled = externalSettings.enabled === undefined ? true : !!externalSettings.enabled;

            this.form.agent.endpoint = agentSettings.endpoint || '';
            this.form.agent.version = agentSettings.version || '1.0.0';
            this.form.agent.secret = agentSettings.secret || '';

            this.form.repository.provider = repositorySettings.provider || 'github';
            this.form.repository.branch = repositorySettings.branch || 'main';
            this.form.repository.url = repositorySettings.url || '';
            this.form.repository.token = repositorySettings.token || '';

            const assignments = this.site.assignments || {};
            this.selectedCustomerIds = Array.isArray(assignments.customer_ids)
                ? assignments.customer_ids.map((item) => Number(item)).filter((item) => Number.isInteger(item) && item > 0)
                : [];
            this.selectedEmployeeIds = Array.isArray(assignments.employee_ids)
                ? assignments.employee_ids.map((item) => Number(item)).filter((item) => Number.isInteger(item) && item > 0)
                : [];

            this.generateAgentCode();
        },
        buildDefaultUrl(domain) {
            if (!domain) {
                return '';
            }
            const normalized = String(domain).trim();
            if (normalized.startsWith('http://') || normalized.startsWith('https://')) {
                return normalized;
            }
            return 'https://' + normalized;
        },
        toIntOrDefault(value, defaultValue) {
            const normalized = Number(value);
            if (!Number.isFinite(normalized)) {
                return defaultValue;
            }
            return Math.floor(normalized);
        },
        goBack() {
            if (this.site) {
                this.$router.push('/workspace/insights/server-monitoring/' + this.site.id + '/manage');
                return;
            }
            this.$router.push('/workspace/insights/server-monitoring');
        },
        saveSettings(silent = false) {
            if (!this.site) {
                return Promise.resolve(false);
            }

            const self = this;
            const payload = {
                website: {
                    title: self.form.website.title,
                    environment: self.form.website.environment,
                },
                external: {
                    url: self.form.external.url,
                    interval_seconds: self.toIntOrDefault(self.form.external.interval_seconds, 300),
                    timeout_ms: self.toIntOrDefault(self.form.external.timeout_ms, 5000),
                    tls_warn_days: self.toIntOrDefault(self.form.external.tls_warn_days, 14),
                    enabled: !!self.form.external.enabled,
                },
                agent: {
                    endpoint: self.form.agent.endpoint,
                    version: self.form.agent.version,
                    secret: self.form.agent.secret,
                },
                repository: {
                    provider: self.form.repository.provider,
                    branch: self.form.repository.branch,
                    url: self.form.repository.url,
                    token: self.form.repository.token,
                },
                customer_ids: self.selectedCustomerIds,
                employee_ids: self.selectedEmployeeIds,
            };

            return axios.put('api/workspace/insights/server-monitoring/' + self.site.id, payload).then(function (response) {
                if (response.data && response.data.item) {
                    self.site = response.data.item;
                    self.applySiteDefaults();
                }
                if (!silent && self.$notify) {
                    self.$notify({
                        title: self.$i18n ? self.$i18n.t('Success').toString() : 'Success',
                        text: self.$t('Settings saved').toString(),
                        type: 'success'
                    });
                }
                return true;
            }).catch(function () {
                if (!silent && self.$notify) {
                    self.$notify({
                        title: self.$i18n ? self.$i18n.t('Error').toString() : 'Error',
                        text: self.$t('Settings could not be saved').toString(),
                        type: 'error'
                    });
                }
                return false;
            });
        },
        toggleActionDropdown(key) {
            const isOpen = !!this.actions[key];
            this.actions = {
                employees: false,
                customers: false,
            };
            this.actions[key] = !isOpen;
        },
        closeActionDropdown() {
            this.actions = {
                employees: false,
                customers: false,
            };
        },
        onEmployeeAssignmentsChange() {
            this.selectedEmployeeIds = this.selectedEmployeeIds
                .map((item) => Number(item))
                .filter((item) => Number.isInteger(item) && item > 0);
            this.autoSaveAssignments(this.$t('Employee assignment saved').toString());
        },
        onCustomerAssignmentsChange() {
            this.selectedCustomerIds = this.selectedCustomerIds
                .map((item) => Number(item))
                .filter((item) => Number.isInteger(item) && item > 0);
            this.autoSaveAssignments(this.$t('Customer assignment saved').toString());
        },
        autoSaveAssignments(successMessage) {
            if (!this.site || !this.site.id) {
                return;
            }

            if (this.assignmentSaving) {
                this.assignmentSavePending = true;
                this.assignmentPendingMessage = successMessage;
                return;
            }

            this.assignmentSaving = true;
            const self = this;
            self.saveSettings(true).then(function (saved) {
                if (saved) {
                    self.$notify({
                        title: self.$i18n ? self.$i18n.t('Success').toString() : 'Success',
                        text: successMessage,
                        type: 'success'
                    });
                    return;
                }

                self.$notify({
                    title: self.$i18n ? self.$i18n.t('Error').toString() : 'Error',
                    text: self.$t('Assignment could not be saved').toString(),
                    type: 'error'
                });
            }).finally(function () {
                self.assignmentSaving = false;
                if (self.assignmentSavePending) {
                    const pendingMessage = self.assignmentPendingMessage || successMessage;
                    self.assignmentSavePending = false;
                    self.assignmentPendingMessage = '';
                    self.autoSaveAssignments(pendingMessage);
                }
            });
        },
        resolveAssignmentAvatar(item) {
            if (item && item.avatar) {
                return item.avatar;
            }
            return '/images/default/icon.png';
        },
        runRepoCheck() {
            const self = this;
            if (!self.site || !self.site.id) {
                return;
            }
            if (!self.form.repository.url || !self.form.repository.token) {
                self.repoCheckMessage = self.$t('Please provide repository URL and read token').toString();
                return;
            }

            self.repoCheckMessage = self.$t('Repository analysis is running').toString() + ' ...';
            self.saveSettings(true).then(function (saved) {
                if (!saved) {
                    self.repoCheckMessage = self.$t('Saving failed. Please check your input').toString();
                    return;
                }

                axios.post('api/workspace/insights/server-monitoring/' + self.site.id + '/refresh').then(function (response) {
                    if (response.data && response.data.item) {
                        self.site = response.data.item;
                        self.applySiteDefaults();
                    }
                    const framework = self.site && self.site.repository ? self.site.repository.framework : null;
                    self.repoCheckMessage = framework
                        ? self.$t('Analysis successful. Framework detected: :framework', {framework}).toString()
                        : self.$t('Analysis successful. No clear framework detection').toString();
                }).catch(function () {
                    self.repoCheckMessage = self.$t('Repository analysis could not be executed').toString();
                });
            });
        },
        generateAgentCode() {
            const token = this.form.agent.secret || 'REPLACE_WITH_SECURE_TOKEN';
            const version = this.form.agent.version || '1.0.0';
            const siteLabel = this.site ? this.site.id : 'unknown-site';

            if (this.agentLanguage === 'python') {
                this.generatedAgentCode = this.buildPythonAgent(token, version, siteLabel);
                return;
            }

            this.generatedAgentCode = this.buildPhpAgent(token, version, siteLabel);
        },
        downloadAgentCode() {
            if (!this.generatedAgentCode) {
                this.generateAgentCode();
            }

            const extension = this.agentLanguage === 'python' ? 'py' : 'php';
            const fileName = 'monitor-agent.' + extension;
            const blob = new Blob([this.generatedAgentCode], {type: 'text/plain;charset=utf-8'});
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');

            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        },
        buildPhpAgent(token, version, siteLabel) {
            return `<?php
// Minimal monitoring agent (safe runtime data only)
declare(strict_types=1);

header('Content-Type: application/json; charset=utf-8');

$agentVersion = '${version}';
$siteId = '${siteLabel}';
$expectedToken = '${token}';

try {
    $authHeader = $_SERVER['HTTP_AUTHORIZATION'] ?? '';
    $providedToken = preg_replace('/^Bearer\\s+/i', '', (string)$authHeader);

    if ($expectedToken !== '' && hash_equals($expectedToken, $providedToken) === false) {
        http_response_code(401);
        echo json_encode(['error' => 'Unauthorized']);
        exit;
    }

    echo json_encode([
        'site_id' => $siteId,
        'agent_version' => $agentVersion,
        'php_version' => PHP_VERSION,
        'server_software' => $_SERVER['SERVER_SOFTWARE'] ?? 'unknown',
        'server_time' => gmdate('c'),
    ], JSON_UNESCAPED_SLASHES);
} catch (Throwable $e) {
    http_response_code(500);
    echo json_encode(['error' => 'Agent execution failed']);
}
`;
        },
        buildPythonAgent(token, version, siteLabel) {
            return `#!/usr/bin/env python3
# Minimal monitoring agent (safe runtime data only)
import json
import os
from datetime import datetime, timezone
from http.server import BaseHTTPRequestHandler, HTTPServer

AGENT_VERSION = "${version}"
SITE_ID = "${siteLabel}"
EXPECTED_TOKEN = "${token}"


class Handler(BaseHTTPRequestHandler):
    def do_GET(self):
        try:
            auth_header = self.headers.get("Authorization", "")
            provided_token = auth_header.replace("Bearer ", "", 1).strip()

            if EXPECTED_TOKEN and provided_token != EXPECTED_TOKEN:
                self.send_response(401)
                self.send_header("Content-Type", "application/json")
                self.end_headers()
                self.wfile.write(json.dumps({"error": "Unauthorized"}).encode("utf-8"))
                return

            payload = {
                "site_id": SITE_ID,
                "agent_version": AGENT_VERSION,
                "php_version": None,
                "server_software": os.environ.get("SERVER_SOFTWARE", "unknown"),
                "server_time": datetime.now(timezone.utc).isoformat(),
                "python_version": os.sys.version.split()[0],
            }

            self.send_response(200)
            self.send_header("Content-Type", "application/json")
            self.end_headers()
            self.wfile.write(json.dumps(payload).encode("utf-8"))
        except Exception:
            self.send_response(500)
            self.send_header("Content-Type", "application/json")
            self.end_headers()
            self.wfile.write(json.dumps({"error": "Agent execution failed"}).encode("utf-8"))


if __name__ == "__main__":
    port = int(os.environ.get("MONITOR_AGENT_PORT", "9811"))
    server = HTTPServer(("0.0.0.0", port), Handler)
    server.serve_forever()
`;
        }
    }
}
</script>
